// LogViewerPage – two-step log explorer with modal preview dialog.

import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import RefreshButton from '../components/shared/RefreshButton';
import { PAGE_TAB_HEADER } from '../components/shared/ui';
import TableLoadingOverlay from '../components/shared/TableLoadingOverlay';
import DataExplorerPane from '../components/LogViewer/panels/DataExplorerPane';
import LogBrowseFilters from '../components/LogViewer/panels/LogBrowseFilters';
import LogFilesPortal from '../components/LogViewer/panels/LogFilesPortal';
import LogPreviewDialog from '../components/LogViewer/dialogs/LogPreviewDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import type { LogFileListScope, LogObjectInfo, LogType } from '../components/LogViewer/types/LogBrowseTypes';
import { getLogFileName } from '../components/LogViewer/utils/logBrowseFileUtils';
import type { ExplorerDataset } from '../components/LogViewer/types/DataExplorerTypes';
import {
    buildDeviceMappingColumns,
    resolveDeviceMappingId,
    type DeviceMappingRow,
} from '../helpers/deviceMappingRows';
import { parseFirmwareListPayload } from '../helpers/firmwareArtifacts';
import { showPrefixedToastError, showToastError, showToastErrorMessage } from '../helpers/toastError';

// ---- Types ----

/** Treat HTTP 500 from log list APIs as empty list (message from main process includes `HTTP 500`). */
function isLikelyHttp500Error(err: unknown): boolean {
    const e = err as { status?: number; message?: string };
    if (e?.status === 500) return true;
    const msg = String(e?.message ?? err ?? '');
    return /HTTP\s*500\b/i.test(msg);
}

type Props = {
    defaultDeviceId?: string;
    defaultLogType?: string;
    operatorScopedEventId?: string | null;
    operatorScopedEventName?: string | null;
    defaultViewMode?: 'logs' | 'data';
};

// ---- Helpers ----

function downloadCsvFile(fileName: string, rows: Record<string, unknown>[]): void {
    const headersSet = new Set<string>();
    rows.forEach((row) => {
        Object.keys(row || {}).forEach((key) => headersSet.add(key));
    });
    const headers = Array.from(headersSet);

    const escapeCsv = (value: unknown): string => {
        const raw = value == null ? '' : (typeof value === 'string' ? value : JSON.stringify(value));
        if (/[",\n\r]/.test(raw)) {
            return `"${raw.replace(/"/g, '""')}"`;
        }
        return raw;
    };

    const lines: string[] = [];
    lines.push(headers.map((h) => escapeCsv(h)).join(','));
    rows.forEach((row) => {
        const line = headers.map((h) => escapeCsv((row || {})[h])).join(',');
        lines.push(line);
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
}

function toRows(data: unknown): Record<string, unknown>[] {
    if (Array.isArray(data)) {
        return data
            .filter((row) => row && typeof row === 'object')
            .map((row) => row as Record<string, unknown>);
    }
    if (data && typeof data === 'object') {
        const obj = data as Record<string, unknown>;
        if (Array.isArray(obj.rows)) return toRows(obj.rows);
        if (Array.isArray(obj.events)) return toRows(obj.events);
        if (Array.isArray(obj.races)) return toRows(obj.races);
        if (Array.isArray(obj.devices)) return toRows(obj.devices);
        if (Array.isArray(obj.operators)) return toRows(obj.operators);
        if (Array.isArray(obj.users)) return toRows(obj.users);
        if (Array.isArray(obj.objects)) return toRows(obj.objects);
        if (Array.isArray(obj.history)) return toRows(obj.history);
        return [obj];
    }
    return [];
}

function parseCsvLine(line: string): string[] {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        if (ch === '"') {
            const next = i + 1 < line.length ? line[i + 1] : '';
            if (inQuotes && next === '"') {
                cur += '"';
                i += 1;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (ch === ',' && !inQuotes) {
            out.push(cur);
            cur = '';
        } else {
            cur += ch;
        }
    }
    out.push(cur);
    return out;
}

function parseCsvToRows(csvText: string): Record<string, unknown>[] {
    const normalized = String(csvText || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
    if (!normalized) return [];
    const lines = normalized.split('\n');
    if (lines.length === 0) return [];
    const headers = parseCsvLine(lines[0]).map((h) => String(h || '').trim());
    if (headers.length === 0) return [];
    const rows: Record<string, unknown>[] = [];
    for (let i = 1; i < lines.length; i += 1) {
        const line = lines[i];
        if (!line) continue;
        const cells = parseCsvLine(line);
        const row: Record<string, unknown> = {};
        headers.forEach((h, idx) => {
            row[h || `col${idx + 1}`] = cells[idx] ?? '';
        });
        rows.push(row);
    }
    return rows;
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

/** Dynamo explorer: first page size (fast paint); total rows cap (memory / UX). */
const DYNAMO_EXPLORER_PAGE = 500;
const DYNAMO_EXPLORER_MAX = 25_000;
const DEVICE_NOTES_EXPLORER_MAX = 5000;

/** Client-side filter: any column value contains the substring (lowercased query). */
function rowMatchesGlobalSearch(row: Record<string, unknown>, qLower: string): boolean {
    if (!qLower) return true;
    for (const v of Object.values(row)) {
        const s =
            v == null
                ? ''
                : typeof v === 'string'
                  ? v
                  : typeof v === 'number' || typeof v === 'boolean'
                    ? String(v)
                    : JSON.stringify(v);
        if (s.toLowerCase().includes(qLower)) return true;
    }
    return false;
}

const LOG_FOLDER_FETCH_CHUNK = 10;

/** Merge folder file lists; dedupe by S3 key; newest first. */
function mergeLogFileRows(lists: LogObjectInfo[][]): LogObjectInfo[] {
    const map = new Map<string, LogObjectInfo>();
    for (const list of lists) {
        for (const o of list) {
            const k = String(o?.key || '').trim();
            if (!k) continue;
            const cur = map.get(k);
            const t = o?.lastModifiedEpochMs ?? 0;
            if (!cur || t > (cur.lastModifiedEpochMs ?? 0)) {
                map.set(k, o);
            }
        }
    }
    return Array.from(map.values()).sort(
        (a, b) => (b.lastModifiedEpochMs ?? 0) - (a.lastModifiedEpochMs ?? 0),
    );
}

// ---- Component ----

export default function LogViewerPage(props: Props): JSX.Element {
    const {
        defaultDeviceId,
        defaultLogType,
        operatorScopedEventId = null,
        operatorScopedEventName = null,
        defaultViewMode,
    } = props;
    const { isAdmin, isOperator } = useAuth();
    const [viewMode, setViewMode] = useState<'logs' | 'data'>(defaultViewMode ?? 'logs');

    const initialLogType: LogType = defaultLogType === 'debug' ? 'debug' : 'race';

    // Folder list (left pane): source list, filter text, loading.
    const [folders, setFolders] = useState<string[]>([]);
    const [folderFilter, setFolderFilter] = useState('');
    const [busyFolders, setBusyFolders] = useState(false);

    // Selected device and log type (race vs debug) for the middle pane.
    const [deviceId, setDeviceId] = useState(defaultDeviceId ?? '');
    const [logType, setLogType] = useState<LogType>(initialLogType);
    const [logFolder, setLogFolder] = useState('');
    const [logFileListScope, setLogFileListScope] = useState<LogFileListScope>('by_event');

    // Log files for selected device: list, selected S3 key.
    const [objects, setObjects] = useState<LogObjectInfo[]>([]);
    const [selectedKey, setSelectedKey] = useState<string>('');

    // Preview (right pane): fixed full preview fetch mode and loading states.
    const [busyList, setBusyList] = useState(false);
    const [busyPreview, setBusyPreview] = useState(false);
    const [busyDownload, setBusyDownload] = useState(false);
    const [busyBulkDownload, setBusyBulkDownload] = useState(false);
    const [busyBulkDelete, setBusyBulkDelete] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [logListEmptyDueToServerError, setLogListEmptyDueToServerError] = useState(false);
    const [filtersDetailsOpen, setFiltersDetailsOpen] = useState(true);
    const [showFilesPortal, setShowFilesPortal] = useState(false);
    const [bulkProgress, setBulkProgress] = useState<{ completed: number; total: number }>({
        completed: 0,
        total: 0,
    });

    const [preview, setPreview] = useState('');
    const [showPreviewDialog, setShowPreviewDialog] = useState(false);

    // Data explorer state.
    const [dataset, setDataset] = useState<ExplorerDataset>('events');
    const [explorerRows, setExplorerRows] = useState<Record<string, unknown>[]>([]);
    const [deviceIdSearch, setDeviceIdSearch] = useState('');
    const [athleteAssignmentsRaceSearch, setAthleteAssignmentsRaceSearch] = useState('');
    const [userEmailBySub, setUserEmailBySub] = useState<Record<string, string>>({});
    const [explorerBusy, setExplorerBusy] = useState(false);
    const [explorerBackfillBusy, setExplorerBackfillBusy] = useState(false);
    const [explorerScanCapped, setExplorerScanCapped] = useState(false);
    const explorerLoadGenRef = useRef(0);
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [createUserSaving, setCreateUserSaving] = useState(false);
    const [createUserForm, setCreateUserForm] = useState({
        username: '',
        email: '',
        givenName: '',
        familyName: '',
        temporaryPassword: '',
        group: 'operator',
    });

    const operatorSelectedEventLabel = useMemo(() => {
        const eventId = String(operatorScopedEventId ?? '').trim();
        const eventName = String(operatorScopedEventName ?? '').trim();
        if (!eventId) return '';
        return eventName ? `${eventName} (${eventId})` : eventId;
    }, [operatorScopedEventId, operatorScopedEventName]);

    const [adminViewEventId, setAdminViewEventId] = useState('');
    const [adminEventsForLogs, setAdminEventsForLogs] = useState<{ eventId: string; name: string }[]>([]);
    const [adminEventsForLogsBusy, setAdminEventsForLogsBusy] = useState(false);
    const [logRaceIdFilter, setLogRaceIdFilter] = useState('');
    const [logRaceOptions, setLogRaceOptions] = useState<Array<{ raceId: string; name: string }>>([]);
    const [logRaceOptionsBusy, setLogRaceOptionsBusy] = useState(false);

    const effectiveLogEventId = useMemo(() => {
        if (isOperator && !isAdmin) {
            const id = String(operatorScopedEventId ?? '').trim();
            return id || undefined;
        }
        if (isAdmin) {
            const id = String(adminViewEventId ?? '').trim();
            return id || undefined;
        }
        return undefined;
    }, [isAdmin, isOperator, operatorScopedEventId, adminViewEventId]);

    const canUseEventMode = useMemo(() => {
        if (isOperator && !isAdmin) return Boolean(effectiveLogEventId);
        return isAdmin;
    }, [isAdmin, isOperator, effectiveLogEventId]);

    const folderScopeEventId = useMemo(() => {
        if (isOperator && !isAdmin) return effectiveLogEventId;
        if (isAdmin && logFileListScope === 'by_event') return effectiveLogEventId;
        return undefined;
    }, [isAdmin, isOperator, logFileListScope, effectiveLogEventId]);

    const canFilterLogsByRace = useMemo(() => {
        if (!effectiveLogEventId) return false;
        if (logFileListScope === 'by_event' || logFileListScope === 'no_folder') return true;
        return logFileListScope === 'by_folder' && Boolean(folderScopeEventId);
    }, [effectiveLogEventId, logFileListScope, folderScopeEventId]);

    const effectiveLogRaceId = useMemo(() => {
        if (!canFilterLogsByRace) return '';
        return String(logRaceIdFilter || '').trim();
    }, [canFilterLogsByRace, logRaceIdFilter]);

    const adminViewEventLabel = useMemo(() => {
        const id = String(adminViewEventId ?? '').trim();
        if (!id) return '';
        const row = adminEventsForLogs.find((e) => e.eventId === id);
        return row?.name ? `${row.name} (${id})` : id;
    }, [adminViewEventId, adminEventsForLogs]);

    /** Short label for “event index” column in the browse summary. */
    const logBrowseEventHeadline = useMemo(() => {
        if (isOperator && !isAdmin) {
            return operatorSelectedEventLabel || 'No event selected';
        }
        if (isAdmin) {
            return adminViewEventId.trim() ? adminViewEventLabel : 'Fleet-wide (all events)';
        }
        return '—';
    }, [isAdmin, isOperator, adminViewEventId, adminViewEventLabel, operatorSelectedEventLabel]);

    const logBrowseEventDetail = useMemo(() => {
        if (isOperator && !isAdmin) {
            return operatorSelectedEventLabel
                ? 'Choose whether step 2 should show all files for this event, or all files for one selected folder.'
                : 'Choose an event on the Events tab to load indexed folders and files.';
        }
        if (isAdmin) {
            return adminViewEventId.trim()
                ? 'Choose whether step 2 should show all files for this event, or all files from one folder under this event.'
                : 'Pick an event to enable the event-wide file mode, or use folder mode fleet-wide.';
        }
        return '';
    }, [isAdmin, isOperator, adminViewEventId, operatorSelectedEventLabel]);

    const logBrowseFolderHeadline = useMemo(() => {
        if (logFileListScope === 'by_event' || logFileListScope === 'fleet_wide') {
            return folders.length ? `All ${folders.length} folder(s)` : 'None (no folders)';
        }
        if (logFileListScope === 'no_folder') return 'No folder';
        const f = logFolder.trim();
        return f ? `“${f}”` : 'None selected';
    }, [logFileListScope, folders.length, logFolder]);

    const logBrowseFolderDetail = useMemo(() => {
        if (logFileListScope === 'by_event' || logFileListScope === 'fleet_wide') {
            return folders.length
                ? 'The file list merges every indexed folder in this scope (event or fleet-wide).'
                : 'Load folders first, or switch to folder mode and select a folder.';
        }
        if (logFileListScope === 'no_folder') {
            return 'The file list only shows uploads with no folder tag.';
        }
        const f = logFolder.trim();
        if (f) {
            return `The file list is only objects under folder “${f}”.`;
        }
        return 'Choose one folder.';
    }, [logFileListScope, folders.length, logFolder]);

    const logBrowseFoldersPaneHint = useMemo(() => {
        if (isOperator && !isAdmin) {
            return operatorSelectedEventLabel
                ? `Folders for ${operatorSelectedEventLabel}. Click one to switch to folder mode.`
                : 'Select an event first.';
        }
        if (isAdmin) {
            return adminViewEventId.trim()
                ? `Folders tagged for ${adminViewEventLabel}. Click one to switch to folder mode.`
                : 'Fleet-wide folder names. Pick one when using folder mode.';
        }
        return '';
    }, [isAdmin, isOperator, adminViewEventId, adminViewEventLabel, operatorSelectedEventLabel]);

    const logBrowseFilesPaneHint = useMemo(() => {
        if (logFileListScope === 'by_event') {
            return folders.length
                ? `Showing all indexed files for this event, across ${folders.length} folder(s).`
                : 'No folders loaded in this scope yet.';
        }
        if (logFileListScope === 'fleet_wide') {
            return 'Showing all files across every folder for this log type.';
        }
        if (logFileListScope === 'no_folder') {
            return 'Showing only files uploaded without a folder.';
        }
        const f = logFolder.trim();
        if (f) {
            return `Showing all files in folder “${f}”. Click a filename to open preview.`;
        }
        return 'Select a folder to load files.';
    }, [logFileListScope, folders.length, logFolder]);

    const logBrowseTargetSummary = useMemo(() => {
        const raceSuffix = effectiveLogRaceId ? ` · race ${effectiveLogRaceId}` : '';
        if (logFileListScope === 'by_folder') {
            return (logFolder.trim() || '—') + raceSuffix;
        }
        if (logFileListScope === 'by_event') {
            return logBrowseEventHeadline + raceSuffix;
        }
        if (logFileListScope === 'fleet_wide') {
            return 'Entire fleet';
        }
        return effectiveLogEventId ? `${logBrowseEventHeadline} · no folder${raceSuffix}` : 'Fleet · no folder';
    }, [logFileListScope, logFolder, logBrowseEventHeadline, effectiveLogEventId, effectiveLogRaceId]);

    const explorerColumns = useMemo(() => {
        if (dataset === 'deviceMappings') {
            return buildDeviceMappingColumns(explorerRows as DeviceMappingRow[]);
        }
        const keys = new Set<string>();
        for (const row of explorerRows) {
            Object.keys(row).forEach((k) => keys.add(k));
        }
        return Array.from(keys);
    }, [dataset, explorerRows]);

    const deviceIdColumn = useMemo(() => {
        const normalized = explorerColumns.map((k) => ({
            key: k,
            n: String(k || '').trim().toLowerCase().replace(/\s+/g, ''),
        }));
        const exact = normalized.find((x) => x.n === 'name' || x.n === 'deviceid' || x.n === 'device_id');
        if (exact) return exact.key;
        const fallback = normalized.find((x) => x.n.includes('device') && x.n.includes('id'));
        return fallback?.key || '';
    }, [explorerColumns]);

    const filteredExplorerRows = useMemo(() => {
        const qDevice = deviceIdSearch.trim().toLowerCase();
        const qRace = athleteAssignmentsRaceSearch.trim().toLowerCase();

        if (dataset === 'eventAthleteAssignments') {
            if (!qDevice && !qRace) return explorerRows;
            return explorerRows.filter((row) => {
                const okDevice = !qDevice || rowMatchesGlobalSearch(row, qDevice);
                const okRace = !qRace || rowMatchesGlobalSearch(row, qRace);
                return okDevice && okRace;
            });
        }

        const query = qDevice;
        if (!query) return explorerRows;
        if (dataset === 'users' || dataset === 'eventDeviceAssignments' || dataset === 'deviceNotes') {
            return explorerRows.filter((row) => rowMatchesGlobalSearch(row, query));
        }
        if (dataset === 'eventRaces') {
            return explorerRows.filter((row) => {
                const eid = String(row?.eventId ?? '').toLowerCase();
                const rid = String(row?.raceId ?? '').toLowerCase();
                const nm = String(row?.name ?? '').toLowerCase();
                return eid.includes(query) || rid.includes(query) || nm.includes(query);
            });
        }
        if (!deviceIdColumn) return explorerRows;
        return explorerRows.filter((row) => String(row?.[deviceIdColumn] ?? '').toLowerCase().includes(query));
    }, [athleteAssignmentsRaceSearch, dataset, deviceIdColumn, deviceIdSearch, explorerRows]);

    const filteredFolders = useMemo(() => {
        const q = folderFilter.trim().toLowerCase();
        if (!q) return folders;
        return folders.filter((f) => f.toLowerCase().includes(q));
    }, [folders, folderFilter]);


    /** Resolve selected key to full object (for size/modified in preview header). */
    const selectedObj = useMemo(() => {
        if (!selectedKey) return undefined;
        return objects.find((o) => (o.key ?? '') === selectedKey);
    }, [objects, selectedKey]);

    /** Load list of folders from log index. */
    const refreshFolders = useCallback(async function refreshFoldersInner(): Promise<void> {
        setBusyFolders(true);
        try {
            const list = await window.fleetAPI.listLogFolders({
                logType,
                eventId: folderScopeEventId,
                raceId: effectiveLogRaceId || undefined,
            });
            const sorted = (list || []).slice().sort((a, b) =>
                a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
            );
            setFolders(sorted);

            setLogFolder((prev) => {
                const cur = String(prev || '').trim();
                if (cur && sorted.includes(cur)) return cur;
                return sorted[0] || '';
            });
        } catch (err: any) {
            showPrefixedToastError('Failed to load folders', err);
            setFolders([]);
            setLogFolder('');
        } finally {
            setBusyFolders(false);
        }
    }, [logType, folderScopeEventId, effectiveLogRaceId]);

    useEffect(() => {
        if (isAdmin) return;
        if (logFileListScope !== 'fleet_wide') return;
        setLogFileListScope('by_event');
    }, [isAdmin, logFileListScope]);

    useEffect(() => {
        if (isAdmin || !isOperator) return;
        const operatorDatasets: ExplorerDataset[] = ['events', 'requests', 'deviceMappings', 'eventDeviceAssignments', 'eventAthleteAssignments', 'firmwareVersions'];
        if (!operatorDatasets.includes(dataset)) {
            setDataset('events');
        }
    }, [isAdmin, isOperator, dataset]);

    useEffect(() => {
        if (logFileListScope !== 'by_folder') return;
        if (!logFolder) return;
        if (filteredFolders.includes(logFolder)) return;
        setLogFolder(filteredFolders[0] || '');
    }, [filteredFolders, logFolder, logFileListScope]);

    /** Load log file list: one folder, or merge all folders in the current scope. */
    const refreshList = useCallback(async function refreshListInner(): Promise<void> {
        setLogListEmptyDueToServerError(false);

        const applyLoaded = (typed: LogObjectInfo[]): void => {
            const sorted = typed.slice().sort(
                (a, b) => (b.lastModifiedEpochMs ?? 0) - (a.lastModifiedEpochMs ?? 0),
            );
            setObjects(sorted);
            setSelectedKeys((prev) => {
                const next = new Set<string>();
                for (const key of prev) {
                    if (typed.some((x) => x.key === key)) next.add(key);
                }
                return next;
            });
            if (typed.length > 0) {
                setSelectedKey((prev) => {
                    if (prev && typed.some((x) => x.key === prev)) return prev;
                    return typed[0].key;
                });
            } else {
                setSelectedKey('');
                setPreview('');
            }
        };

        if (logFileListScope === 'by_folder') {
            const folder = logFolder.trim();
            if (!folder) {
                setObjects([]);
                setSelectedKey('');
                setPreview('');
                return;
            }

            setBusyList(true);
            try {
                const list = await window.fleetAPI.listLogFolderFiles({
                    logType,
                    logFolder: folder,
                    eventId: folderScopeEventId,
                    raceId: effectiveLogRaceId || undefined,
                });
                applyLoaded(list as unknown as LogObjectInfo[]);
            } catch (err: unknown) {
                if (isLikelyHttp500Error(err)) {
                    setLogListEmptyDueToServerError(true);
                } else {
                    showPrefixedToastError('Failed to load log list', err);
                }
                setObjects([]);
                setSelectedKeys(new Set());
                setSelectedKey('');
                setPreview('');
            } finally {
                setBusyList(false);
            }
            return;
        }

        setBusyList(true);
        try {
            if (logFileListScope === 'no_folder') {
                const list = await window.fleetAPI.listLogFolderFiles({
                    logType,
                    eventId: effectiveLogEventId,
                    raceId: effectiveLogRaceId || undefined,
                });
                applyLoaded(list as unknown as LogObjectInfo[]);
                return;
            }

            if (logFileListScope === 'fleet_wide') {
                if (!isAdmin) {
                    applyLoaded([]);
                    return;
                }
                const folderNames = await window.fleetAPI.listLogFolders({ logType });
                const batches: LogObjectInfo[][] = [];
                for (let i = 0; i < folderNames.length; i += LOG_FOLDER_FETCH_CHUNK) {
                    const slice = folderNames.slice(i, i + LOG_FOLDER_FETCH_CHUNK);
                    const settled = await Promise.allSettled(
                        slice.map((name) => window.fleetAPI.listLogFolderFiles({ logType, logFolder: name })),
                    );
                    for (const r of settled) {
                        if (r.status === 'fulfilled') batches.push(r.value as unknown as LogObjectInfo[]);
                    }
                }
                const noFolderList = await window.fleetAPI.listLogFolderFiles({ logType });
                applyLoaded(
                    mergeLogFileRows([
                        ...batches,
                        noFolderList as unknown as LogObjectInfo[],
                    ]),
                );
                return;
            }

            const eventId = String(effectiveLogEventId || '').trim();
            if (!eventId) {
                applyLoaded([]);
                return;
            }
            const list = await window.fleetAPI.listEventIndexedLogFiles({
                eventId,
                logType,
                raceId: effectiveLogRaceId || undefined,
            });
            const noFolderList = await window.fleetAPI.listLogFolderFiles({
                logType,
                eventId,
                raceId: effectiveLogRaceId || undefined,
            });
            applyLoaded(
                mergeLogFileRows([
                    list as unknown as LogObjectInfo[],
                    noFolderList as unknown as LogObjectInfo[],
                ]),
            );
        } catch (err: unknown) {
            if (isLikelyHttp500Error(err)) {
                setLogListEmptyDueToServerError(true);
            } else {
                showPrefixedToastError('Failed to load log list', err);
            }
            setObjects([]);
            setSelectedKeys(new Set());
            setSelectedKey('');
            setPreview('');
        } finally {
            setBusyList(false);
        }
    }, [logFileListScope, logFolder, logType, effectiveLogEventId, folderScopeEventId, effectiveLogRaceId, isAdmin]);

    const handleBulkDownload = useCallback(async (): Promise<void> => {
        if (!window.fleetAPI?.bulkDownloadSelectedLogs) {
            window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Bulk download is not available' }));
            return;
        }

        const keys = Array.from(selectedKeys);
        if (!keys.length) {
            window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Select at least one file' }));
            return;
        }

        setBusyBulkDownload(true);
        setBulkProgress({ completed: 0, total: keys.length });
        try {
            const res = await window.fleetAPI.bulkDownloadSelectedLogs({
                keys,
                eventId: effectiveLogEventId,
            });

            if (res?.canceled) return;
            if (!res?.ok) {
                throw new Error(res?.error || 'Bulk download failed');
            }

            const downloadedCount = Array.isArray(res.downloaded) ? res.downloaded.length : 0;
            const failedCount = Array.isArray(res.failed) ? res.failed.length : 0;
            window.dispatchEvent(
                new CustomEvent('toast-info', {
                    detail: `Downloaded ${downloadedCount}. Failed ${failedCount}.`,
                }),
            );
        } catch (err: any) {
            showToastError(err, 'Bulk download failed');
        } finally {
            setBusyBulkDownload(false);
        }
    }, [effectiveLogEventId, selectedKeys]);

    /** Admin-only: permanently delete the selected log files (S3 + index row each). */
    const handleBulkDelete = useCallback(async (): Promise<void> => {
        if (!window.fleetAPI?.deleteLogFile) {
            window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Log deletion is not available' }));
            return;
        }

        const keys = Array.from(selectedKeys);
        if (!keys.length) {
            window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Select at least one file' }));
            return;
        }

        setBusyBulkDelete(true);
        try {
            const results = await Promise.all(
                keys.map(async (key) => {
                    const res = await window.fleetAPI.deleteLogFile({ key });
                    return { key, ok: !!res?.ok, error: res?.error };
                }),
            );
            const deletedKeys = new Set(results.filter((r) => r.ok).map((r) => r.key));
            const failedCount = results.length - deletedKeys.size;

            if (deletedKeys.size > 0) {
                setObjects((prev) => prev.filter((o) => !deletedKeys.has(o.key)));
                setSelectedKeys((prev) => {
                    const next = new Set(prev);
                    for (const k of deletedKeys) next.delete(k);
                    return next;
                });
                if (deletedKeys.has(selectedKey)) setSelectedKey('');
            }

            window.dispatchEvent(
                new CustomEvent(failedCount > 0 ? 'toast-error' : 'toast-info', {
                    detail:
                        failedCount > 0
                            ? `Deleted ${deletedKeys.size}. Failed ${failedCount}.`
                            : `Deleted ${deletedKeys.size} log file${deletedKeys.size === 1 ? '' : 's'}.`,
                }),
            );
        } catch (err: any) {
            showToastError(err, 'Bulk delete failed');
        } finally {
            setBusyBulkDelete(false);
            setDeleteConfirmOpen(false);
        }
    }, [selectedKey, selectedKeys]);

    /** Fetch head or tail of the selected log file (maxBytes). */
    const refreshPreview = useCallback(
        async function refreshPreviewInner(key: string): Promise<void> {
            const k = (key || '').trim();
            if (!k) {
                setPreview('');
                return;
            }

            setBusyPreview(true);
            try {
                const text = await window.fleetAPI.getLogPreview({ key: k, mode: 'head', maxBytes: 1024 * 1024, eventId: effectiveLogEventId });
                setPreview(text || '');
            } catch (err: any) {
                const msg = err?.message || String(err);
                const isNotFound = msg.includes('key_not_found') || msg.includes('NoSuchKey');
                setPreview(
                    isNotFound
                        ? '[File not found in storage — it may have been deleted or has not yet been uploaded.]'
                        : `[Failed to load preview]\n\n${msg}`,
                );
            } finally {
                setBusyPreview(false);
            }
        },
        [effectiveLogEventId],
    );

    /** Save selected log file via main process (native save dialog). */
    const handleDownload = useCallback(async function handleDownloadInner(): Promise<void> {
        const k = selectedKey.trim();
        if (!k) return;

        setBusyDownload(true);
        try {
            const suggestedName = getLogFileName(k) || 'log.txt';
            const res = await window.fleetAPI.downloadLogFile({
                key: k,
                suggestedName,
                eventId: effectiveLogEventId,
            });

            if (res?.canceled) return;
            if (!res?.ok) throw new Error(res?.error || 'Download failed');

            window.dispatchEvent(
                new CustomEvent('toast-info', {
                    detail: `Saved log: ${res.filePath || suggestedName}`,
                }),
            );
        } catch (err: any) {
            showPrefixedToastError('Download failed', err);
        } finally {
            setBusyDownload(false);
        }
    }, [effectiveLogEventId, selectedKey]);

    const onSelectFolder = useCallback(function onSelectFolderInner(id: string): void {
        setLogFileListScope('by_folder');
        setLogFolder(id);
        setObjects([]);
        setSelectedKeys(new Set());
        setSelectedKey('');
        setPreview('');
    }, []);

    const onSelectKey = useCallback(function onSelectKeyInner(k: string): void {
        setSelectedKey(k);
    }, []);

    const onOpenPreview = useCallback(function onOpenPreviewInner(k: string): void {
        setSelectedKey(k);
        setShowPreviewDialog(true);
    }, []);

    const onToggleKey = useCallback((key: string, checked: boolean): void => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (checked) next.add(key);
            else next.delete(key);
            return next;
        });
    }, []);

    const onReplaceSelectedKeys = useCallback((keys: string[]): void => {
        setSelectedKeys(new Set(keys));
    }, []);

    const refreshExplorer = useCallback(async () => {
        const gen = ++explorerLoadGenRef.current;
        setExplorerScanCapped(false);
        setExplorerBackfillBusy(false);
        setExplorerBusy(true);
        let skipDefaultSet = false;

        const applyRows = (rows: Record<string, unknown>[]): void => {
            setExplorerRows(rows);
        };

        const runPagedDynamoScan = async (tableName: string): Promise<void> => {
            const api = window.eventsAPI?.scanDataExplorerTable;
            if (!api) throw new Error('Data explorer scan is not available');
            let acc: Record<string, unknown>[] = [];
            let nextToken: string | undefined;
            let isFirst = true;
            for (;;) {
                if (explorerLoadGenRef.current !== gen) return;
                const res = await api({
                    tableName,
                    limit: DYNAMO_EXPLORER_PAGE,
                    ...(nextToken ? { nextToken } : {}),
                });
                if (!res?.ok) throw new Error(res?.error || 'Failed to scan table');
                const chunk = toRows(res.data);
                const tokenRaw = (res.data as { nextToken?: string | null })?.nextToken;
                const token = tokenRaw != null && String(tokenRaw).length > 0 ? String(tokenRaw) : null;
                acc = acc.concat(chunk);
                if (explorerLoadGenRef.current !== gen) return;
                if (acc.length >= DYNAMO_EXPLORER_MAX) {
                    setExplorerScanCapped(true);
                    setExplorerRows([...acc]);
                    setExplorerBackfillBusy(false);
                    break;
                }
                if (isFirst) {
                    setExplorerRows(acc);
                    setExplorerBusy(false);
                    setExplorerBackfillBusy(!!token);
                    isFirst = false;
                } else {
                    setExplorerRows([...acc]);
                    setExplorerBackfillBusy(!!token);
                }
                if (!token) {
                    setExplorerBackfillBusy(false);
                    break;
                }
                nextToken = token;
            }
        };

        try {
            let data: unknown = [];
            // Keep a best-effort sub->email map for identity columns across datasets.
            try {
                const usersRes = await window.eventsAPI.listUsers();
                if (usersRes?.ok) {
                    const users = (usersRes.data as any)?.users;
                    const map: Record<string, string> = {};
                    if (Array.isArray(users)) {
                        for (const u of users) {
                            const key = String(u?.userId ?? u?.sub ?? '').trim();
                            const email = String(u?.email ?? '').trim();
                            if (key && email) map[key] = email;
                        }
                    }
                    setUserEmailBySub(map);
                }
            } catch {
                // ignore
            }
            switch (dataset) {
                case 'events': {
                    const res = isOperator && !isAdmin
                        ? await window.eventsAPI.listMyEvents()
                        : await window.eventsAPI.listEvents({ limit: 200 });
                    if (!res?.ok) throw new Error(res?.error || 'Failed to load events');
                    data = res.data;
                    break;
                }
                case 'requests': {
                    const scopedEventId = isOperator && !isAdmin ? (operatorScopedEventId ?? '').trim() : '';
                    if (isOperator && !isAdmin && !scopedEventId) {
                        throw new Error('Select an event on the Events tab to load scoped requests');
                    }
                    let res = scopedEventId
                        ? await window.eventsAPI.listEventRequests(scopedEventId)
                        : await window.eventsAPI.listRequests();
                    if (!res?.ok && Number(res?.status) === 401 && !scopedEventId) {
                        await sleep(700);
                        res = await window.eventsAPI.listRequests();
                    }
                    if (!res?.ok) throw new Error(res?.error || 'Failed to load requests');
                    data = res?.data;
                    break;
                }
                case 'firmwareVersions': {
                    const res = await window.eventsAPI.getFirmwareVersions();
                    if (!res?.ok) throw new Error(res?.error || 'Failed to load firmware versions');
                    const { artifacts } = parseFirmwareListPayload(res, { defaultRole: 'rear' });
                    data = artifacts;
                    break;
                }
                case 'users': {
                    const res = await window.eventsAPI.listUsers();
                    if (!res?.ok) throw new Error(res?.error || 'Failed to load users');
                    data = res.data;
                    break;
                }
                case 'deviceMappings': {
                    const res = await window.eventsAPI.getDeviceMappings();
                    if (!res?.ok) throw new Error(res?.error || 'Failed to load device mappings');
                    data = res.data;
                    break;
                }
                case 'deviceNotes': {
                    skipDefaultSet = true;
                    const api = window.eventsAPI?.listDeviceNotes;
                    if (!api) {
                        throw new Error('Device notes API is not available');
                    }
                    const effectiveDeviceId = (deviceIdSearch.trim() || deviceId).trim();
                    let acc: Record<string, unknown>[] = [];
                    let nextTok: string | undefined;
                    let isFirstNotes = true;
                    for (;;) {
                        if (explorerLoadGenRef.current !== gen) return;
                        const res = await api({
                            ...(effectiveDeviceId ? { deviceId: effectiveDeviceId } : {}),
                            limit: 50,
                            ...(nextTok ? { nextToken: nextTok } : {}),
                        });
                        if (!res?.ok) {
                            throw new Error(res?.error || 'Failed to load device notes');
                        }
                        const raw = (res.data as { notes?: unknown[] })?.notes;
                        const chunk = Array.isArray(raw)
                            ? (raw.filter(
                                (n): n is Record<string, unknown> =>
                                    !!n && typeof n === 'object' && !Array.isArray(n),
                            ) as Record<string, unknown>[])
                            : [];
                        acc = acc.concat(chunk);
                        const tokenRaw = (res.data as { nextToken?: string | null })?.nextToken;
                        const token =
                            tokenRaw != null && String(tokenRaw).length > 0 ? String(tokenRaw) : null;

                        if (explorerLoadGenRef.current !== gen) return;

                        if (acc.length >= DEVICE_NOTES_EXPLORER_MAX) {
                            setExplorerScanCapped(true);
                            setExplorerRows([...acc]);
                            setExplorerBackfillBusy(false);
                            break;
                        }
                        if (isFirstNotes) {
                            setExplorerRows(acc);
                            setExplorerBusy(false);
                            setExplorerBackfillBusy(!!token);
                            isFirstNotes = false;
                        } else {
                            setExplorerRows([...acc]);
                            setExplorerBackfillBusy(!!token);
                        }
                        if (!token) {
                            setExplorerBackfillBusy(false);
                            break;
                        }
                        nextTok = token;
                    }
                    break;
                }
                case 'eventDeviceAssignments': {
                    const effectiveEventId = deviceIdSearch.trim() || (isOperator && !isAdmin ? (operatorScopedEventId ?? '').trim() : '');
                    if (!effectiveEventId) {
                        if (isOperator && !isAdmin) {
                            throw new Error('Select an event on the Events tab to load event device assignments');
                        }
                        const eventsRes = await window.eventsAPI.listEvents({ limit: 200 });
                        if (!eventsRes?.ok) {
                            throw new Error(eventsRes?.error || 'Failed to load events for device assignments');
                        }
                        const events = ((eventsRes.data as any)?.events || []) as Record<string, unknown>[];
                        const merged: Record<string, unknown>[] = [];
                        for (const ev of events) {
                            const eventId = String(ev?.eventId ?? '').trim();
                            if (!eventId) continue;
                            const devRes = await window.eventsAPI.listEventDevices(eventId, { limit: 5000 });
                            if (!devRes?.ok) continue;
                            const devices = ((devRes.data as any)?.devices || []) as Record<string, unknown>[];
                            devices.forEach((row) => {
                                merged.push({
                                    eventId,
                                    eventName: String(ev?.name ?? ''),
                                    eventStatus: String(ev?.status ?? ''),
                                    ...row,
                                });
                            });
                        }
                        data = merged;
                        break;
                    }
                    const res = await window.eventsAPI.listEventDevices(effectiveEventId, { limit: 5000 });
                    if (!res?.ok) {
                        throw new Error(res?.error || 'Failed to load event device assignments');
                    }
                    const devices = ((res.data as any)?.devices || []) as Record<string, unknown>[];
                    data = devices.map((row) => ({ eventId: effectiveEventId, ...row }));
                    break;
                }
                case 'eventRaceDeviceAssignmentHistory': {
                    const tableName = String(window.fmPublicEnv?.RACE_DEVICES_HISTORY_TABLE ?? '').trim();
                    if (!tableName) {
                        throw new Error('RACE_DEVICES_HISTORY_TABLE is not set in environment');
                    }
                    skipDefaultSet = true;
                    await runPagedDynamoScan(tableName);
                    break;
                }
                case 'deviceLifecycle': {
                    const tableName = String(window.fmPublicEnv?.DEVICE_LIFECYCLE_TABLE ?? '').trim();
                    if (!tableName) {
                        throw new Error('DEVICE_LIFECYCLE_TABLE is not set in environment');
                    }
                    skipDefaultSet = true;
                    await runPagedDynamoScan(tableName);
                    break;
                }
                case 'eventRaces': {
                    const tableName = String(window.fmPublicEnv?.EVENT_RACES_TABLE ?? '').trim();
                    if (!tableName) {
                        throw new Error('EVENT_RACES_TABLE is not set in environment');
                    }
                    skipDefaultSet = true;
                    await runPagedDynamoScan(tableName);
                    break;
                }
                case 'eventAthleteAssignments': {
                    const effectiveEventId = deviceIdSearch.trim() || (isOperator && !isAdmin ? (operatorScopedEventId ?? '').trim() : '');
                    const effectiveRaceId = athleteAssignmentsRaceSearch.trim();
                    if (!effectiveEventId) {
                        if (isOperator && !isAdmin) {
                            throw new Error('Select an event on the Events tab to load event athlete assignments');
                        }
                        const eventsRes = await window.eventsAPI.listEvents({ limit: 200 });
                        if (!eventsRes?.ok) {
                            throw new Error(eventsRes?.error || 'Failed to load events for athlete assignments');
                        }
                        const events = ((eventsRes.data as any)?.events || []) as Record<string, unknown>[];
                        const summaries: Record<string, unknown>[] = [];
                        for (const ev of events) {
                            const eventId = String(ev?.eventId ?? '').trim();
                            if (!eventId) continue;
                            const racesRes = await window.eventsAPI.listEventRaces(eventId);
                            if (!racesRes?.ok) continue;
                            const races = ((racesRes.data as any)?.races || []) as Record<string, unknown>[];
                            for (const race of races) {
                                const raceId = String(race?.raceId ?? '').trim();
                                if (!raceId) continue;
                                const metaRes = await window.eventsAPI.getDevicePackageAssignmentsMeta(eventId, {
                                    raceId,
                                });
                                if (!metaRes?.ok) continue;
                                const file = (metaRes.data as any)?.file;
                                if (!file) continue;
                                summaries.push({
                                    eventId,
                                    raceId,
                                    raceName: String(race?.name ?? ''),
                                    eventName: String(ev?.name ?? ''),
                                    eventStatus: String(ev?.status ?? ''),
                                    ...file,
                                });
                            }
                        }
                        data = summaries;
                        break;
                    }
                    if (!effectiveRaceId) {
                        let eventName = '';
                        let eventStatus = '';
                        if (isAdmin) {
                            const eventsRes = await window.eventsAPI.listEvents({ limit: 200 });
                            if (!eventsRes?.ok) throw new Error(eventsRes?.error || 'Failed to load events');
                            const events = ((eventsRes.data as any)?.events || []) as Record<string, unknown>[];
                            const evMeta = events.find((e) => String(e?.eventId ?? '').trim() === effectiveEventId);
                            eventName = String(evMeta?.name ?? '');
                            eventStatus = String(evMeta?.status ?? '');
                        }
                        const racesRes = await window.eventsAPI.listEventRaces(effectiveEventId);
                        if (!racesRes?.ok) {
                            throw new Error(racesRes?.error || 'Failed to load races for event');
                        }
                        const races = ((racesRes.data as any)?.races || []) as Record<string, unknown>[];
                        const summaries: Record<string, unknown>[] = [];
                        for (const race of races) {
                            const raceId = String(race?.raceId ?? '').trim();
                            if (!raceId) continue;
                            const metaRes = await window.eventsAPI.getDevicePackageAssignmentsMeta(effectiveEventId, {
                                raceId,
                            });
                            if (!metaRes?.ok) continue;
                            const file = (metaRes.data as any)?.file;
                            if (!file) continue;
                            summaries.push({
                                eventId: effectiveEventId,
                                raceId,
                                raceName: String(race?.name ?? ''),
                                eventName,
                                eventStatus,
                                ...file,
                            });
                        }
                        data = summaries;
                        break;
                    }
                    const res = await window.eventsAPI.getDevicePackageAssignmentsCsv(effectiveEventId, {
                        raceId: effectiveRaceId,
                        expiresIn: 600,
                    });
                    if (!res?.ok) {
                        if (Number(res?.status) === 404) {
                            data = [];
                            break;
                        }
                        throw new Error(res?.error || 'Failed to load event athlete assignments');
                    }
                    const csvText = String((res.data as any)?.csvText || '');
                    const rows = parseCsvToRows(csvText);
                    data = rows.map((row) => ({
                        eventId: effectiveEventId,
                        raceId: effectiveRaceId,
                        ...row,
                    }));
                    break;
                }
                default:
                    data = [];
            }

            if (explorerLoadGenRef.current !== gen) return;

            if (!skipDefaultSet) {
                applyRows(toRows(data));
            }
        } catch (err: any) {
            setExplorerRows([]);
            setExplorerScanCapped(false);
            showPrefixedToastError('Explorer load failed', err);
        } finally {
            if (explorerLoadGenRef.current === gen) {
                setExplorerBusy(false);
                setExplorerBackfillBusy(false);
            }
        }
    }, [athleteAssignmentsRaceSearch, dataset, deviceId, deviceIdSearch, isAdmin, isOperator, operatorScopedEventId]);

    useEffect(() => {
        if (!isAdmin || viewMode !== 'logs') return;
        let cancelled = false;
        setAdminEventsForLogsBusy(true);
        void (async () => {
            try {
                const res = await window.eventsAPI.listEvents({ limit: 200 });
                if (!res?.ok) throw new Error(res?.error || 'Failed to load events');
                const rows = toRows(res.data)
                    .map((r) => ({
                        eventId: String(r.eventId ?? '').trim(),
                        name: String(r.name ?? '').trim(),
                    }))
                    .filter((r) => r.eventId);
                if (!cancelled) setAdminEventsForLogs(rows);
            } catch {
                if (!cancelled) setAdminEventsForLogs([]);
            } finally {
                if (!cancelled) setAdminEventsForLogsBusy(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAdmin, viewMode]);

    useEffect(() => {
        if (viewMode !== 'logs') return;
        const eventId = String(effectiveLogEventId || '').trim();
        if (!eventId) {
            setLogRaceOptions([]);
            setLogRaceIdFilter('');
            return;
        }

        let cancelled = false;
        setLogRaceOptionsBusy(true);
        void (async () => {
            try {
                const res = await window.eventsAPI.listEventRaces(eventId);
                if (!res?.ok) throw new Error(res?.error || 'Failed to load event races');
                const rows = toRows(res.data)
                    .map((row) => ({
                        raceId: String(row.raceId ?? '').trim(),
                        name: String(row.name ?? '').trim(),
                    }))
                    .filter((row) => row.raceId);
                if (!cancelled) setLogRaceOptions(rows);
            } catch {
                if (!cancelled) {
                    setLogRaceOptions([]);
                }
            } finally {
                if (!cancelled) setLogRaceOptionsBusy(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [effectiveLogEventId, viewMode]);

    useEffect(() => {
        const selected = String(logRaceIdFilter || '').trim();
        if (!selected) return;
        if (logRaceOptions.some((row) => row.raceId === selected)) return;
        setLogRaceIdFilter('');
    }, [logRaceIdFilter, logRaceOptions]);

    /** Load folders on mount / type change. */
    useEffect(() => {
        refreshFolders();
    }, [refreshFolders]);

    /** When folder or log type changes, reload file list for that folder. */
    useEffect(() => {
        refreshList();
    }, [logFolder, logType, logFileListScope, folders, refreshList]);

    useEffect(() => {
        if (!window.fleetAPI?.onBulkDownloadProgress) return;
        const off = window.fleetAPI.onBulkDownloadProgress((data) => {
            setBulkProgress({
                completed: Number(data?.completed || 0),
                total: Number(data?.total || 0),
            });
        });
        return () => {
            off?.();
        };
    }, []);

    /** When selected key or preview options change, reload preview text. */
    useEffect(() => {
        if (!selectedKey) {
            setPreview('');
            setShowPreviewDialog(false);
            return;
        }
        refreshPreview(selectedKey);
    }, [selectedKey, refreshPreview]);

    useEffect(() => {
        if (viewMode !== 'data') return;
        void refreshExplorer();
    }, [dataset, viewMode]);

    const removeExplorerRow = useCallback(async (rowIndex: number) => {
        if (dataset !== 'deviceNotes') return;

        const row = explorerRows[rowIndex] || {};
        const deviceId = String(row.deviceId ?? '').trim();
        const noteId = String(row.noteId ?? '').trim();
        if (!deviceId || !noteId) {
            window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Missing deviceId/noteId for note deletion' }));
            return;
        }

        const res = await window.eventsAPI.deleteDeviceNote({ deviceId, noteId });
        if (!res?.ok) {
            window.dispatchEvent(new CustomEvent('toast-error', { detail: res?.error || 'Failed to delete device note' }));
            return;
        }
        window.dispatchEvent(new CustomEvent('toast-info', { detail: 'Device note deleted' }));
        await refreshExplorer();
    }, [dataset, explorerRows, refreshExplorer]);

    const canCreateUser = useMemo(() => {
        const username = createUserForm.username.trim();
        const email = createUserForm.email.trim();
        const group = createUserForm.group.trim();
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        return username.length > 0 && emailOk && group.length > 0;
    }, [createUserForm]);

    const submitCreateUser = useCallback(async () => {
        if (!canCreateUser) return;
        setCreateUserSaving(true);
        try {
            const res = await window.eventsAPI.createUser({
                username: createUserForm.username.trim(),
                email: createUserForm.email.trim(),
                givenName: createUserForm.givenName.trim() || undefined,
                familyName: createUserForm.familyName.trim() || undefined,
                temporaryPassword: createUserForm.temporaryPassword.trim() || undefined,
                group: createUserForm.group.trim() || undefined,
            });
            if (!res?.ok) throw new Error(res?.error || 'Failed to create user');
            window.dispatchEvent(new CustomEvent('toast-info', { detail: `Created user ${createUserForm.username.trim()}` }));
            setShowCreateUserModal(false);
            setCreateUserForm({
                username: '',
                email: '',
                givenName: '',
                familyName: '',
                temporaryPassword: '',
                group: 'operator',
            });
            await refreshExplorer();
        } catch (err: any) {
            showPrefixedToastError('Create user failed', err);
        } finally {
            setCreateUserSaving(false);
        }
    }, [canCreateUser, createUserForm, refreshExplorer]);

    return (
        <div className="h-full min-h-0 flex flex-col bg-slate-100 dark:bg-slate-800">
            {/* Page header – title and description */}
            <div className={PAGE_TAB_HEADER}>
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="text-lg font-semibold">Log And Data Explorer</div>
                        <div className="text-sm text-gray-500 dark:text-slate-400">
                            Browse S3 logs or inspect API table-style data and export JSON.
                        </div>
                    </div>
                    <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 dark:border-slate-600">
                        <button
                            type="button"
                            className={`px-3 py-2 text-sm ${
                                viewMode === 'logs'
                                    ? 'bg-blue-600 text-white font-medium dark:bg-blue-600'
                                    : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                            }`}
                            onClick={() => setViewMode('logs')}
                        >
                            Logs
                        </button>
                        <button
                            type="button"
                            className={`px-3 py-2 text-sm border-l border-gray-300 dark:border-slate-600 ${
                                viewMode === 'data'
                                    ? 'bg-blue-600 text-white font-medium dark:bg-blue-600'
                                    : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                            }`}
                            onClick={() => setViewMode('data')}
                        >
                            Data Explorer
                        </button>
                    </div>
                </div>
            </div>

            {isOperator && !isAdmin && (
                <div className="px-4 pt-2">
                    <div className="flex gap-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-3 py-2 text-xs text-gray-700">
                        <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-gray-500 dark:text-slate-400" aria-hidden />
                        <div className="min-w-0 space-y-0.5">
                            <p className="text-sm font-medium text-gray-900 dark:text-slate-100">Operator scope</p>
                            <p className="leading-relaxed text-gray-600 dark:text-slate-400">
                                {operatorSelectedEventLabel
                                    ? `Logs are scoped to ${operatorSelectedEventLabel}.`
                                    : 'Select an event on the Events tab to load indexed logs.'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex-1 min-h-0 overflow-auto p-4">
                {viewMode === 'logs' ? (
                    <div className="flex flex-col gap-4">
                        <LogBrowseFilters
                            open={filtersDetailsOpen}
                            onOpenChange={setFiltersDetailsOpen}
                            isAdmin={isAdmin}
                            isOperator={isOperator}
                            canUseEventMode={canUseEventMode}
                            logFileListScope={logFileListScope}
                            onLogFileListScopeChange={setLogFileListScope}
                            logType={logType}
                            onLogTypeChange={setLogType}
                            logFolder={logFolder}
                            onLogFolderChange={setLogFolder}
                            filteredFolders={filteredFolders}
                            onRefreshFolders={refreshFolders}
                            busyFolders={busyFolders}
                            adminViewEventId={adminViewEventId}
                            onAdminViewEventIdChange={setAdminViewEventId}
                            adminEventsForLogs={adminEventsForLogs}
                            adminEventsForLogsBusy={adminEventsForLogsBusy}
                            operatorSelectedEventLabel={operatorSelectedEventLabel}
                            effectiveLogEventId={effectiveLogEventId}
                            showRaceFilter={canFilterLogsByRace}
                            selectedRaceId={logRaceIdFilter}
                            onSelectedRaceIdChange={setLogRaceIdFilter}
                            raceOptions={logRaceOptions}
                            raceOptionsBusy={logRaceOptionsBusy}
                            logBrowseTargetSummary={logBrowseTargetSummary}
                            selectedKeysCount={selectedKeys.size}
                            objectCount={objects.length}
                            busyList={busyList}
                            onViewFiles={() => setShowFilesPortal(true)}
                        />
                        {showFilesPortal && <LogFilesPortal
                            open={showFilesPortal}
                            objects={objects}
                            selectedKeys={selectedKeys}
                            selectedKey={selectedKey}
                            busyList={busyList}
                            busyBulkDownload={busyBulkDownload}
                            bulkProgress={bulkProgress}
                            filesScopeHint={logBrowseFilesPaneHint}
                            onClose={() => setShowFilesPortal(false)}
                            onRefreshList={refreshList}
                            onToggleKey={onToggleKey}
                            onReplaceSelectedKeys={onReplaceSelectedKeys}
                            onSelectKey={onSelectKey}
                            onOpenPreview={onOpenPreview}
                            onDownload={() => void handleBulkDownload()}
                            onDelete={isAdmin ? () => setDeleteConfirmOpen(true) : undefined}
                            busyBulkDelete={busyBulkDelete}
                        />}
                        <ConfirmDialog
                            open={deleteConfirmOpen}
                            danger
                            busy={busyBulkDelete}
                            title="Delete log files"
                            message={
                                <>
                                    Permanently delete <span className="font-semibold">{selectedKeys.size}</span> selected
                                    log file{selectedKeys.size === 1 ? '' : 's'}? This cannot be undone.
                                </>
                            }
                            confirmText="Delete"
                            // LogFilesPortal renders its own overlay at z-[60]; the default
                            // Dialog z-index (50) would render this confirm behind it.
                            overlayZIndex={100}
                            onConfirm={() => void handleBulkDelete()}
                            onCancel={() => setDeleteConfirmOpen(false)}
                        />
                    </div>
                ) : (
                    <DataExplorerPane
                        isAdmin={isAdmin}
                        dataset={dataset}
                        onDatasetChange={setDataset}
                        deviceIdSearch={deviceIdSearch}
                        onDeviceIdSearchChange={setDeviceIdSearch}
                        athleteAssignmentsRaceSearch={athleteAssignmentsRaceSearch}
                        onAthleteAssignmentsRaceSearchChange={setAthleteAssignmentsRaceSearch}
                        explorerBusy={explorerBusy}
                        explorerBackfillBusy={explorerBackfillBusy}
                        explorerScanCapped={explorerScanCapped}
                        onRefresh={() => void refreshExplorer()}
                        onDownloadCsv={() => downloadCsvFile(`${dataset}.csv`, explorerRows)}
                        hasAnyRows={explorerRows.length > 0}
                        filteredExplorerRows={filteredExplorerRows}
                        explorerRows={explorerRows}
                        explorerColumns={explorerColumns}
                        userEmailBySub={userEmailBySub}
                        deviceIdColumn={deviceIdColumn}
                        onRemoveRow={removeExplorerRow}
                    />
                )}
            </div>
            <LogPreviewDialog
                open={showPreviewDialog}
                selectedKey={selectedKey}
                selectedObj={selectedObj}
                preview={preview}
                busyPreview={busyPreview}
                busyDownload={busyDownload}
                onReloadPreview={() => refreshPreview(selectedKey)}
                onDownload={handleDownload}
                onClose={() => setShowPreviewDialog(false)}
            />
        </div>
    );
}
